import React, { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../../contexts/ToastContext.jsx';
import { ApiError } from '../../utils/apiClient.js';
import { parseApiError } from '../../utils/consolidatedErrorHandling.js';
import { apiRequest } from '../../utils/apiClient.js';

/**
 * Modal for confirming user reactivation
 */
const UserReactivationModal = ({ isOpen, user, onClose, onReactivated }) => {
  const { getToken } = useAuth();
  const { showToast } = useToast();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [sendNotification, setSendNotification] = useState(true);
  const [resetPassword, setResetPassword] = useState(false);

  /**
   * Handle reactivation confirmation
   */
  const handleReactivate = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      
      const response = await apiRequest(`/api/admin/users/${user.id}/reactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          notes: notes.trim(),
          send_notification: sendNotification,
          reset_password: resetPassword
        })
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const result = await response.json();
      
      showToast('User reactivated successfully', 'success');
      onReactivated(result);
      handleClose();

    } catch (error) {
      console.error('Error reactivating user:', error);
      showToast(error.message || 'Failed to reactivate user', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reset form and close modal
   */
  const handleClose = () => {
    setNotes('');
    setSendNotification(true);
    setResetPassword(false);
    onClose();
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center mb-4">
          <div className="w-10 h-10 bg-green-900/30 rounded-full flex items-center justify-center mr-3">
            <span className="text-green-400 text-lg">✅</span>
          </div>
          <div>
            <h3 className="text-lg font-medium text-white dark:text-white">Reactivate User</h3>
            <p className="text-sm text-gray-400">
              {user.first_name} {user.last_name} ({user.email})
            </p>
          </div>
        </div>

        {/* Info Message */}
        <div className="bg-green-900/20 border border-green-500/50 rounded-lg p-4 mb-4">
          <p className="text-green-300 text-sm">
            This action will restore the user's access to their account and all associated services. They will be able to log in and use the platform normally.
          </p>
        </div>

        {/* Form */}
        <div className="space-y-4">
          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2 dark:text-gray-600">
              Reactivation notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Enter any notes about the reactivation..."
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-green-500 resize-none dark:text-white"
              rows={3}
              maxLength={500}
            />
            <div className="text-xs text-gray-400 mt-1">
              {notes.length}/500 characters
            </div>
          </div>

          {/* Options */}
          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="send_notification"
                checked={sendNotification}
                onChange={(e) => setSendNotification(e.target.checked)}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-600 rounded bg-gray-700"
              />
              <label htmlFor="send_notification" className="ml-2 text-sm text-gray-300 dark:text-gray-600">
                Send welcome back email to user
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="reset_password"
                checked={resetPassword}
                onChange={(e) => setResetPassword(e.target.checked)}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-600 rounded bg-gray-700"
              />
              <label htmlFor="reset_password" className="ml-2 text-sm text-gray-300 dark:text-gray-600">
                Force password reset on next login
              </label>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors disabled:opacity-50 dark:text-white"
          >
            Cancel
          </button>
          <button
            onClick={handleReactivate}
            disabled={loading}
            className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50 dark:text-white"
          >
            {loading ? 'Reactivating...' : 'Reactivate User'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserReactivationModal;
